import { useEffect, useRef, useState } from 'react';

interface WorkerMessage {
  type: 'TICK';
  seconds: number;
  isFinished: boolean;
}

interface WorkerCommand {
  type: 'START' | 'STOP';
  remainSeconds?: number;
}

/**
 * Custom hook for countdown timer using Web Worker
 * Provides accurate timing independent of React render cycles
 */
export const useTimerWorker = (initialSeconds: number, onFinish: () => void) => {
  const [seconds, setSeconds] = useState(initialSeconds);
  const workerRef = useRef<Worker | null>(null);
  const onFinishRef = useRef(onFinish);

  // Keep onFinish callback up to date
  useEffect(() => {
    onFinishRef.current = onFinish;
  }, [onFinish]);

  // Initialize worker
  useEffect(() => {
    if (typeof Worker === 'undefined') {
      console.warn('Web Workers not supported, falling back to main thread');
      return;
    }

    const worker = new Worker(`${process.env.PUBLIC_URL}/workers/timer.js`);
    workerRef.current = worker;

    // Handle messages from worker
    worker.onmessage = (e: MessageEvent<WorkerMessage>) => {
      const { type, seconds: newSeconds, isFinished } = e.data;

      if (type === 'TICK') {
        setSeconds(newSeconds);
        if (isFinished) {
          onFinishRef.current();
        }
      }
    };

    // Handle worker errors
    worker.onerror = error => {
      console.error('Worker error:', error);
    };

    return () => {
      if (workerRef.current) {
        workerRef.current.postMessage({ type: 'STOP' });
        workerRef.current.terminate();
        workerRef.current = null;
      }
    };
  }, []);

  // Start timer when initialSeconds changes
  useEffect(() => {
    if (workerRef.current) {
      workerRef.current.postMessage({
        type: 'START',
        remainSeconds: initialSeconds
      });
    } else {
      // Fallback to main thread timer if workers not supported
      const endTime = Date.now() + initialSeconds * 1000;
      const interval = setInterval(() => {
        const now = Date.now();
        const remainingSeconds = Math.max(0, Math.ceil((endTime - now) / 1000));
        setSeconds(remainingSeconds);

        if (remainingSeconds === 0) {
          clearInterval(interval);
          onFinishRef.current();
        }
      }, 1000);

      return () => clearInterval(interval);
    }
  }, [initialSeconds]);

  return seconds;
};
